import React from 'react';
import { Space, Button, Dropdown, Tooltip, Row, Modal, notification, Tag, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, KeyOutlined, ReloadOutlined, RobotOutlined, UploadOutlined } from '@ant-design/icons';
import { downloadAllCustomProductAmazonGenerations } from '../../../api/downloadAllCustomProductAmazonGenerations';
import { downloadCustomProductInputs } from '../../../api/downloadCustomProductInputs';
import { Company } from '../../../store/companySlice';
import { downloadCustomProductInputTemplate } from '../../../utils/downloadCustomProductInputTemplate';
import { generateAmazonContentForCustomProducts } from '../../../api/generateAmazonContentForCustomProduct';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { assignBrandKeywordsToCustomProducts } from '../../../api/assignBrandKeywordsToCustomProduct';
import { CustomProductBulkTarget } from '../../../types/customProductBulkActions';

interface TableActionsProps {
    selectedCompany: Company | null;
    selectedRowKeys: React.Key[];
    bulkTarget: CustomProductBulkTarget;
    hasActiveFilters: boolean;
    setUploadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleRefreshProducts: () => void;
}

const TableActions: React.FC<TableActionsProps> = ({ selectedCompany, selectedRowKeys, bulkTarget, hasActiveFilters, setUploadModalOpen, setDeleteModalOpen, handleRefreshProducts }) => {
    const storage = getStorage()
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [generating, setGenerating] = React.useState<boolean>(false);
    const [assigningKeywords, setAssigningKeywords] = React.useState<boolean>(false);


    const scopeLabel = React.useMemo(() => {
        if (bulkTarget.scope === 'selected') {
            return `${selectedRowKeys.length} selected`;
        }
        if (bulkTarget.scope === 'filtered') {
            return 'filtered products';
        }
        return 'all products';
    }, [bulkTarget.scope, selectedRowKeys.length]);

    const scopeTag = React.useMemo(() => {
        if (bulkTarget.scope === 'selected') {
            return <Tag color="processing">{selectedRowKeys.length} selected</Tag>;
        }
        if (bulkTarget.scope === 'filtered') {
            return <Tag color="warning">Filtered subset</Tag>;
        }
        return null;
    }, [bulkTarget.scope, selectedRowKeys.length]);

    const handleGeneratProductsContent = () => {
        if (!selectedCompany) return;

        Modal.confirm({
            title: `Generate Content for ${scopeLabel}`,
            content: `Are you sure you want to generate content for ${scopeLabel}? This may take a while and consume your quota.`,
            okText: 'Yes, generate',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setGenerating(true);
                    const result = await generateAmazonContentForCustomProducts(selectedCompany.id, bulkTarget);
                    const requestedCount = result.requestedCount ?? (bulkTarget.scope === 'all' ? selectedCompany.customProductCount : undefined) ?? result.scheduledCount;

                    if (result.scheduledCount === 0) {
                        notification.warning({
                            message: 'No generations started',
                            description: result.message ?? 'The company has no remaining custom content quota.',
                        });
                        return;
                    }

                    if (result.quotaReached) {
                        notification.warning({
                            message: 'Quota reached',
                            description: `Started ${result.scheduledCount} of ${requestedCount} custom content generations before the company quota was exhausted.`,
                        });
                        return;
                    }

                    notification.success({
                        message: 'Generation started',
                        description: `Started ${result.scheduledCount} custom content generations for ${scopeLabel}.`,
                    });
                } catch (error) {
                    console.error('Content generation failed:', error);
                    notification.error({
                        message: 'Generation failed',
                        description: 'Custom content generation could not be started.',
                    });
                } finally {
                    setGenerating(false);
                }
            },
        });
    };

    const handleAssignKeywordsToProducts = () => {
        if (!selectedCompany) return;

        Modal.confirm({
            title: `Assign Keywords to ${scopeLabel}`,
            content: `Queue keyword assignment for ${scopeLabel} with completed brand research? This may take a while.`,
            okText: 'Yes, assign',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setAssigningKeywords(true);
                    const result = await assignBrandKeywordsToCustomProducts(selectedCompany.id, bulkTarget);

                    if (result.scheduledCount === 0) {
                        notification.warning({
                            message: 'No assignments started',
                            description: result.message ?? 'No products had completed brand research ready for keyword assignment.',
                        });
                        return;
                    }

                    notification.success({
                        message: 'Keyword assignment started',
                        description: `Queued keyword assignment for ${result.scheduledCount} products${result.skippedCount ? ` and skipped ${result.skippedCount}` : ''}.`,
                    });
                    handleRefreshProducts();
                } catch (error) {
                    console.error('Keyword assignment failed:', error);
                    notification.error({
                        message: 'Keyword assignment failed',
                        description: 'Product keyword assignment could not be started.',
                    });
                } finally {
                    setAssigningKeywords(false);
                }
            },
        });
    };

    const handleDownloadAllCustomProductAmazonGenerations = async () => {
        if (selectedCompany) {
            setDownloading(true);
            try {
                const { filePath } = await downloadAllCustomProductAmazonGenerations(
                    selectedCompany.id,
                    bulkTarget,
                )
                const fileRef = ref(storage, filePath)
                const url = await getDownloadURL(fileRef)

                window.open(url, '_blank');
            } catch (error) {
                console.error('Error downloading custom product generations:', error);
            } finally {
                setDownloading(false);
            }
        }
    }

    const handleDownloadCustomProductInputs = async () => {
        if (selectedCompany) {
            setDownloading(true);
            try {
                const { filePath } = await downloadCustomProductInputs(
                    selectedCompany.id,
                    bulkTarget,
                )
                const fileRef = ref(storage, filePath)
                const url = await getDownloadURL(fileRef)

                window.open(url, '_blank');
            } catch (error) {
                console.error('Error downloading custom product inputs:', error);
                notification.error({
                    message: 'Download failed',
                    description: 'Custom product inputs could not be downloaded.',
                });
            } finally {
                setDownloading(false);
            }
        }
    }


    const handleMenuClick = (action: string, key: React.Key) => {
        if (action === 'download') {
            if (key === 'all') {
                handleDownloadAllCustomProductAmazonGenerations();
            } else if (key === 'input') {
                handleDownloadCustomProductInputs();
            } else if (key === 'template') {
                downloadCustomProductInputTemplate();
            }
        } else if (action === 'analyze') {
            if (key === 'all') {
                handleGeneratProductsContent();
            }
        }
    };

    const downloadMenu = {
        items: [
            {
                key: 'all',
                label: 'Download Generated Content',
            },
            {
                key: 'input',
                label: `Download Product Input (${scopeLabel})`,
            },
            {
                key: 'template',
                label: 'Download Input Template',
            },
        ],
        onClick: ({ key }: { key: React.Key }) => handleMenuClick('download', key),
    };

    return (
        <Row justify="end">
            <Space size="small">
                {scopeTag}
                {hasActiveFilters && bulkTarget.scope === 'selected' ? (
                    <Typography.Text type="secondary">Filters ignored for selected rows</Typography.Text>
                ) : null}
                <Space.Compact>
                    <Tooltip title="Upload Product Input">
                        <Button
                            type="default"
                            icon={<UploadOutlined />}
                            onClick={() => setUploadModalOpen(true)}
                        />
                    </Tooltip>
                    <Tooltip title="Download">
                        <Dropdown menu={downloadMenu}>
                            <Tooltip title="Download Options">
                                <Button icon={<DownloadOutlined />} disabled={downloading} loading={downloading} />
                            </Tooltip>
                        </Dropdown>
                    </Tooltip>
                </Space.Compact>
                <Tooltip title={`Generate Content for ${scopeLabel}`}>
                    <Button
                        disabled={generating}
                        loading={generating}
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={handleGeneratProductsContent}
                    />
                </Tooltip>
                <Tooltip title={`Assign Keywords to ${scopeLabel}`}>
                    <Button
                        disabled={assigningKeywords || generating}
                        loading={assigningKeywords}
                        type="default"
                        icon={<KeyOutlined />}
                        onClick={handleAssignKeywordsToProducts}
                    />
                </Tooltip>
                <Space.Compact>
                    <Tooltip title="Refresh">
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={handleRefreshProducts}
                        />
                    </Tooltip>
                    <Tooltip title={`Delete ${scopeLabel}`}>
                        <Button
                            variant="solid"
                            color='danger'
                            icon={<DeleteOutlined />}
                            onClick={() => setDeleteModalOpen(true)}
                        />
                    </Tooltip>
                </Space.Compact>
            </Space>
        </Row>
    );
};

export default TableActions;
